
/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import lottie from 'lottie-web'
import { Box } from '@mui/material'

import { usePageTitle } from 'apps/utils/usePageTitle'
import homeAnimation from './home-animation.json'

// Frame where the intro hands off to the static menu (~5s @ 24fps).
const HANDOFF_FRAME = 100

const Home = () => {
	usePageTitle( 'ACT Software — 10 Years of SaaS Development' )
	const container = useRef( null )
	const [ isComplete, setIsComplete ] = useState( false )
	const history = useHistory()
	const year = new Date().getFullYear()
	const animRef = useRef( null )

	const armHandoff = ( anim ) => {
		const onFrame = () => {
			if ( anim.currentFrame >= HANDOFF_FRAME ) {
				anim.removeEventListener( 'enterFrame', onFrame )
				anim.pause()
				setIsComplete( true )
			}
		}
		anim.addEventListener( 'enterFrame', onFrame )
	}

	// (Re)create the intro animation from scratch and play it from frame 0.
	const startIntro = () => {
		if ( animRef.current ) animRef.current.destroy()
		const anim = lottie.loadAnimation( {
			container: container.current,
			renderer: 'svg',
			autoplay: true,
			loop: false,
			animationData: homeAnimation
		} )
		animRef.current = anim
		armHandoff( anim )
		anim.addEventListener( 'complete', () => setIsComplete( true ) )   // fallback if the frame is never reached
	}

	// Clicking the central pentagon softly restarts the intro (no page reload).
	const replayIntro = () => {
		setIsComplete( false )
		startIntro()
	}

	useEffect( () => {
		const targets = [
			{ id: 'Layer_6', path: 'open-source' },
			{ id: 'Layer_4', path: 'tech-stack' },
			{ id: 'Layer_2', path: 'ai' },
			{ id: 'Layer_5', path: 'act' },
			{ id: 'Layer_7', path: 'team' }
		]
		const bound = targets
			.map( ( { id, path } ) => {
				const el = document.getElementById( id )
				if ( !el ) return null
				const handler = () => history.push( path )
				el.addEventListener( 'click', handler )
				return { el, handler }
			} )
			.filter( Boolean )

		// Central pentagon (logo): replay the intro animation.
		const center = document.getElementById( 'Layer_3' )
		const centerHandler = () => replayIntro()
		if ( center ) center.addEventListener( 'click', centerHandler )

		return () => {
			bound.forEach( ( { el, handler } ) => el.removeEventListener( 'click', handler ) )
			if ( center ) center.removeEventListener( 'click', centerHandler )
		}
	}, [ history ] )

	useEffect( () => {
		// Play the intro and hand off to the static menu once the grid has
		// unfolded (HANDOFF_FRAME); the later teardown/shrink is skipped. NB: the
		// lottie's central logo is drawn slightly larger than the static one
		// (asset mismatch), so a small logo "pop" can remain at the cross-fade —
		// to be resolved when the animation assets are reworked.
		startIntro()
		return () => { if ( animRef.current ) animRef.current.destroy() }
	}, [] )

	return (
		<Box display='flex' alignItems='center' height={ isComplete ? '100vh' : 'auto' } bgcolor='#323232'>
			<Box
				sx={ {
					opacity: isComplete ? 1 : 0,
					pointerEvents: isComplete ? 'auto' : 'none',
					transition: 'opacity 0.6s ease-in-out',
					width: '100%',
					position: 'absolute',
					top: 0,
					bottom: 0,
					left: 0,
					right: 0
				} }
				display='flex'
				alignItems='center'
			>
				<svg  x="0px" y="0px" viewBox="0 0 1920 989" style={{ enableBackground: 'new 0 0 1920 989', margin: 'auto' }} xmlSpace="preserve">
					<g id="Layer_1">
							<g>
									<defs>
											<rect id="SVGID_1_" y="0" width="1920" height="989"/>
									</defs>
									<clipPath id="SVGID_00000043414862164197286610000013805342100403234442_">
											<use xlinkHref="#SVGID_1_"  style={{ overflow:'visible' }}/>
									</clipPath>
											<rect y="0" style={{ clipPath:'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill:'#323232', }} width="1920" height="989"/>
											<radialGradient id="SVGID_00000008130348060311545580000012564703318458098867_" cx="362.4266" cy="913.7595" r="1" gradientTransform="matrix(121.7684 141.0168 152.5213 -131.7025 -182202.1094 69438.9219)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
											<path
													style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000008130348060311545580000012564703318458098867_)', fillOpacity:'0.2' }} d="
													M1306,58l-134.1,97.6l51.2,157.5h165.3l51.6-157.7L1306,58z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M1306,58l-134.1,97.6
													l51.2,157.5h165.3l51.6-157.7L1306,58z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fillOpacity: '0.2' }} d="M1306,58
													l-134.1,97.6l51.2,157.5h165.3l51.6-157.7L1306,58z"/>
									
											<radialGradient id="SVGID_00000133520738273173811910000014444749880524389015_" cx="362.4157" cy="913.756" r="1" gradientTransform="matrix(121.3111 140.0241 151.5191 -131.2699 -180985.2344 69247.4219)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
											
										<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000133520738273173811910000014444749880524389015_)', fillOpacity: '0.2' }} d="
											M1357-98l-51.1,155.9l134,97.4l133.1-97L1522-98H1357z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M1357-98l-51.1,155.9
													l134,97.4l133.1-97L1522-98H1357z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fillOpacity: '0.2' }} d="M1357-98
													l-51.1,155.9l134,97.4l133.1-97L1522-98H1357z"/>
									
											<radialGradient id="SVGID_00000106851669149202918150000013706929938028630455_" cx="362.4137" cy="913.7559" r="1" gradientTransform="matrix(121.2744 139.8402 151.3423 -131.2495 -182211.7812 70312.6016)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
										<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000106851669149202918150000013706929938028630455_)', fillOpacity: '0.2' }} d="
											M-44.7,919.1l-51,156.4l133.5,96.6l133.5-96.6l-51-156.4H-44.7z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M-44.7,919.1l-51,156.4
													l133.5,96.6l133.5-96.6l-51-156.4H-44.7z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fillOpacity: '0.2' }} d="M-44.7,919.1
											l-51,156.4l133.5,96.6l133.5-96.6l-51-156.4H-44.7z"/>
									
											<radialGradient id="SVGID_00000002379281694239533900000010783526400735145140_" cx="362.4258" cy="913.7591" r="1" gradientTransform="matrix(121.7284 140.9456 152.4477 -131.6622 -183172 70191.3594)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000002379281694239533900000010783526400735145140_)', fillOpacity: '0.2' }} d="
											M254.2,821.5l-134,97.4l51.2,157.6H337l51.2-157.6L254.2,821.5z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M254.2,821.5l-134,97.4
													l51.2,157.6H337l51.2-157.6L254.2,821.5z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fillOpacity: '0.2' }} d="M254.2,821.5
											l-134,97.4l51.2,157.6H337l51.2-157.6L254.2,821.5z"/>
									
											<radialGradient id="SVGID_00000136373687141333452470000010900823947855029897_" cx="362.4258" cy="913.7591" r="1" gradientTransform="matrix(121.7284 140.9456 152.4477 -131.6622 -182822.0312 69937.8984)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000136373687141333452470000010900823947855029897_)', fillOpacity: '0.2' }} d="
											M604.2,568l-134,97.4L522,823h165l51.2-157.6L604.2,568z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M604.2,568l-134,97.4
													L522,823h165l51.2-157.6L604.2,568z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)',  fillOpacity: '0.2' }} d="M604.2,568
											l-134,97.4L522,823h165l51.2-157.6L604.2,568z"/>
									
											<radialGradient id="SVGID_00000075856493785231038980000007559299130327890839_" cx="362.4258" cy="913.7591" r="1" gradientTransform="matrix(121.7284 140.9456 152.4477 -131.6622 -182471.2031 69937.8984)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000075856493785231038980000007559299130327890839_)', fillOpacity: '0.2' }} d="
											M872.2,568L821,725.6L955,823l134-97.4L1037.8,568H872.2z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M872.2,568L821,725.6
											L955,823l134-97.4L1037.8,568H872.2z"/>
									
											<radialGradient id="SVGID_00000143617549958750152590000005738046305044847242_" cx="362.152" cy="913.7831" r="1" gradientTransform="matrix(120.7168 117.7035 127.3186 -130.578 -159104.375 77153.5312)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442)', fill: 'url(#SVGID_00000143617549958750152590000005738046305044847242_)', fillOpacity: '0.2' }} d="
											M955,313l-134,97.4L872.2,568h165.6l51.2-157.6L955,313z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M955,313l-134,97.4
											L872.2,568h165.6l51.2-157.6L955,313z"/>
									
											<radialGradient id="SVGID_00000063614775075839945850000015845034866562177665_" cx="362.4276" cy="913.7589" r="1" gradientTransform="matrix(121.7283 141.1082 152.5985 -131.6405 -182475.0938 69446.5781)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
										<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442)', fill: 'url(#SVGID_00000063614775075839945850000015845034866562177665_)', fillOpacity: '0.2' }} d="
											M1006.2,155.5l-51.2,157.6l134,97.7l134-97.7l-51.2-157.6H1006.2z"/>
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M1006.2,155.5
											l-51.2,157.6l134,97.7l134-97.7l-51.2-157.6H1006.2z"/>
									
											<radialGradient id="SVGID_00000121971830811605098410000002409943484653669530_" cx="362.422" cy="913.7597" r="1" gradientTransform="matrix(121.7283 140.5875 152.1164 -131.7106 -181467.0156 69348.1719)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000121971830811605098410000002409943484653669530_)', fillOpacity: '0.2' }} d="
											M1656-196l-134,97.4l51,157h166.1l50.9-157L1656-196z"/>
									<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M1656-196l-134,97.4
											l51,157h166.1l50.9-157L1656-196z"/>
									<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fillOpacity: '0.2' }} d="M1656-196
											l-134,97.4l51,157h166.1l50.9-157L1656-196z"/>
									
											<radialGradient id="SVGID_00000062175184846299776890000005372013781954750116_" cx="362.4101" cy="913.7587" r="1" gradientTransform="matrix(121.472 139.4949 151.0544 -131.538 -181670.4375 70447.7969)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
															<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
															<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
										<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000062175184846299776890000005372013781954750116_)', fillOpacity: '0.2' }} d="
											M305,665l-51,156.4l133.5,96l133.9-94.5L470,665H305z"/>
									<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M305,665l-51,156.4
											l133.5,96l133.9-94.5L470,665H305z"/>
									<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)',  fillOpacity: '0.2' }} d="M305,665
											l-51,156.4l133.5,96l133.9-94.5L470,665H305z"/>
									
											<path style= {{ opacity:'0.3', clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fillRule: 'evenodd', clipRule: 'evenodd', fill: '#D9D9D9', enableBackground: 'new' }} d="
											M269.1,324l0.8,0.6l169.1-169.1h-1.4L269.1,324z M412.5,155.5h1.4L255.5,313.9l-0.8-0.6L412.5,155.5z M387.3,155.5h1.4
											L265.6,278.6l0.7-2.1L387.3,155.5z M362.1,155.5h1.4l-85.8,85.8l0.7-2.1L362.1,155.5z M336.9,155.5h1.4L289.8,204l0.7-2.1
											L336.9,155.5z M311.7,155.5h1.4l-11.2,11.2l0.7-2.1L311.7,155.5z M462.9,155.5L283.6,334.8l0.8,0.6l179.8-179.8H462.9z M474.6,169
											L298.1,345.5l0.8,0.6l176-176L474.6,169z M480.7,188L312.5,356.2l0.8,0.6l167.7-167.7L480.7,188z M486.9,207L327,366.9l0.8,0.6
											l159.4-159.4L486.9,207z M493.1,226L341.5,377.6l0.8,0.6l151.1-151.1L493.1,226z M499.3,245L356,388.3l0.8,0.6l142.8-142.8
											L499.3,245z M505.4,264l-135,135l0.8,0.6l134.5-134.5L505.4,264z M511.6,283.1L384.9,409.7l0.8,0.6L512,284.1L511.6,283.1z
											M517.8,302.1l-53.3,53.3l5.5-4.1l48.1-48.1L517.8,302.1z"/>
									
											<path style= {{ opacity:'0.3', clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fillRule: 'evenodd', clipRule: 'evenodd', fill: '#D9D9D9', enableBackground: 'new' }} d="
											M1729.5,768.9l-0.8,0.6l160,160l0.3-1.1L1729.5,768.9z M1895.2,909.4l-0.4,1.1l-151.6-151.6l0.8-0.6L1895.2,909.4z M1901.3,890.3
											l-0.3,1.1l-143.3-143.3l0.8-0.6L1901.3,890.3z M1907.5,871.3l-0.3,1.1l-135-135l0.8-0.6L1907.5,871.3z M1913.7,852.3l-0.3,1.1
											l-126.7-126.7l0.8-0.6L1913.7,852.3z M1919.8,833.2l-0.3,1.1l-50-50l5.1,3.7L1919.8,833.2z M1882.9,947.4L1715,779.6l-0.8,0.6
											l168.3,168.3L1882.9,947.4z M1876.7,966.5l-176.2-176.2l-0.8,0.6l176.6,176.6L1876.7,966.5z M1865.8,980.8L1686.1,801l-0.8,0.6
											l179.2,179.2H1865.8z M1840.6,980.8l-169.1-169.1l-0.8,0.6l168.5,168.5H1840.6z M1815.5,980.8l-158.4-158.4l-0.8,0.6L1814,980.8
											H1815.5z M1790.3,980.8l-123-123l0.7,2.1l120.9,120.9H1790.3z M1765.1,980.8l-85.8-85.8l0.7,2.1l83.7,83.7H1765.1z M1739.9,980.8
											l-48.6-48.5l0.7,2.1l46.4,46.5H1739.9z M1714.7,980.8l-11.3-11.3l0.7,2.1l9.2,9.2H1714.7z"/>
									
											<path style= {{ opacity:'0.3', clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)',fillRule: 'evenodd', clipRule: 'evenodd', fill: '#D9D9D9', enableBackground: 'new' }} d="
											M1626.2,801.3l-0.8-0.6l-179.9,179.9h1.4L1626.2,801.3z M1472,980.7h-1.4l169.3-169.3l0.8,0.6L1472,980.7z M1497.2,980.7h-1.4
											l158.7-158.7l0.8,0.6L1497.2,980.7z M1522.4,980.7h-1.4l124.2-124.2l-0.7,2.1L1522.4,980.7z M1547.6,980.7h-1.4l86.9-86.9
											l-0.7,2.1L1547.6,980.7z M1572.8,980.7h-1.4L1621,931l-0.7,2.1L1572.8,980.7z M1597.6,980.7h-1.4l12.9-12.9l-0.7,2.1L1597.6,980.7
											z M1434.9,967.4l176.7-176.6l-0.8-0.6l-176.2,176.2L1434.9,967.4z M1428.8,948.4L1597,780.1l-0.8-0.6l-167.8,167.8L1428.8,948.4z
											M1422.6,929.4l159.8-159.8l-0.8-0.6l-159.4,159.4L1422.6,929.4z M1416.4,910.3l151.4-151.4l-0.8-0.6l-150.9,150.9L1416.4,910.3z
											M1410.2,891.3l143-143l-0.8-0.6l-142.5,142.5L1410.2,891.3z M1404.1,872.3l134.6-134.6l-0.8-0.6l-134.1,134.1L1404.1,872.3z
											M1397.9,853.3l126.2-126.2l-0.8-0.6l-125.7,125.7L1397.9,853.3z M1391.7,834.3l50.8-50.8l-5.2,3.8l-46,46L1391.7,834.3z"/>
									
											<path style= {{ opacity:'0.3', clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)',fillRule: 'evenodd', clipRule: 'evenodd', fill: '#D9D9D9', enableBackground: 'new' }} d="
											M1538.2,737.4l0.8,0.6l169.8-169.8h-1.4L1538.2,737.4z M1682.2,568.2h1.4l-159.2,159.2l-0.8-0.6L1682.2,568.2z M1657,568.2h1.4
											l-126.2,126.2l0.7-2.1L1657,568.2z M1631.8,568.2h1.4l-88.9,88.9l0.7-2.1L1631.8,568.2z M1606.7,568.2h1.4l-51.5,51.5l0.7-2.1
											L1606.7,568.2z M1581.5,568.2h1.4l-14.2,14.2l0.7-2.1L1581.5,568.2z M1732.6,568.2L1552.8,748l0.8,0.6L1734,568.2H1732.6z
											M1742.7,583.3l-175.3,175.3l0.8,0.6l174.8-174.8L1742.7,583.3z M1748.8,602.4L1582,769.2l0.8,0.6l166.3-166.3L1748.8,602.4z
											M1754.9,621.5l-158.3,158.3l0.8,0.6l157.8-157.8L1754.9,621.5z M1761,640.6l-149.8,149.8l0.8,0.6l149.3-149.3L1761,640.6z
											M1767,659.7L1625.8,801l0.8,0.6l140.8-140.8L1767,659.7z M1773.1,678.8l-132.8,132.8l0.8,0.6l132.3-132.3L1773.1,678.8z
											M1779.2,698l-124.3,124.3l0.8,0.6L1779.5,699L1779.2,698z M1785.3,717.1l-40.8,40.8l5.4-4l35.7-35.7L1785.3,717.1z"/>
									
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fillRule: 'evenodd', clipRule: 'evenodd', fill: '#C0BDC2' }} d="
											M1789.8-99.4L1656-196.6l-134.6,97.8l0.1,0.3h-164.9l-51.2,156.3l-132.7,96.7l50.8-156.3l0.1-0.3l-0.3-0.2l-134-97.4l-0.3-0.2
											l-0.3,0.2L955-2.7L821.3-99.9l-0.3-0.2l-0.3,0.2l-134,97.4l-0.3,0.2l0.1,0.3l50.7,156.1L604.8,58.8l50.8-157.2l0.1-0.3l-0.3-0.2
											L522-196.3l-0.3-0.2l-0.3,0.2L387.9-98.9l-0.3,0.2l0.1,0.3l0.1,0.5H223.1h-0.4l-0.1,0.3L171.4,57.6l-0.2,0.1L38.8,154L89.4-1.9
											l0.1-0.3l-0.3-0.2l-134-97.4l-0.3-0.2l-0.3,0.2l-134,97.4l-0.3,0.2l0.1,0.3l51.2,157.6l0.1,0.3h0.1l-50.6,157l-0.1,0.3l0.3,0.2
											l133.5,96.6l0.3,0.2l0.3-0.2l133.4-96.6h164l-133.1,97.9H-44.9h-0.4l-0.1,0.3l-51,157.4l-0.1,0.3l0.3,0.2l131.6,94.3H-128h-0.4
											l-0.1,0.3l-51,156.7l-0.1,0.3l0.3,0.2l133.5,96.6l0.3,0.2l0.3-0.2L88,822h0.1h164.6l-132.9,96.6H-45.1l-51.2,157.1l134.1,97.1
											l133.1-96.3l0.2,0.5h166.4l51.4-158.3l-0.7-0.5L521,824.4l-50.5,154.8l-0.1,0.3l0.3,0.2l133.5,96.6l0.3,0.2l0.3-0.2l132.8-96.1
											l0.1,0.5l0.1,0.3h0.4h165.6h0.4l0.1-0.3L955,824.6l50.7,156.1l0.1,0.3h0.4h165.6h0.4l0.1-0.3l0.2-0.5l132.8,96.1l0.3,0.2l0.3-0.2
											l132.8-96.1l0.2,0.5l0.1,0.3h0.4h165.6h0.4l0.1-0.3l50.7-156.1l50.5,156.2l0.1,0.3h0.4h165h0.4l0.1-0.3l51-157.6l0.1-0.3h164.7
											h0.4l0.1-0.3l51.2-157.6l0.1-0.3l-0.3-0.2l-134-97.4l-0.3-0.2l-0.3,0.2l-0.2,0.1l-64.8-156.4l-0.1-0.3h-0.3h-149.4l-132.2-96.1
											h163.8h0.1l133.6,97.7l0.3,0.2l0.3-0.2l132.9-100.4l0.3-0.2l-0.1-0.3l-55.7-155.3l-0.1-0.3h-0.3h-158.5l131.7-97.1l0.3-0.2
											l-0.1-0.3l-51-157l-0.1-0.3h-0.4h-165L1789.8-99.4L1789.8-99.4z M1822.8,313.6l51-157.1h159.1l55.5,154.6l-132.3,100L1822.8,313.6
											z M1786.2,93.2l86.6,63.4l-51,156.9h-164.9l-20.4-62.8l-30.9-95.1l133.5-96.7h0.3l0,0L1786.2,93.2z M1787.6,92.9l-47.8-35l1.3-4.1
											l49.5-152.2h164.3l50.8,156.3l-132.4,97.6L1787.6,92.9z M1740.2,53.6l49.3-152l-133.4-97l-133.4,97l48,147.8l2.8,8.4h165.4
											L1740.2,53.6z M1656.6,314.5h-0.3L1522.8,411l23.7,73.1l27.2,83.7h163.9l8-24l44-132.5L1656.6,314.5L1656.6,314.5z M1635.9,252
											l20,61.6l-133.7,96.7L1389,312.9l51.5-157.2h164.2L1635.9,252z M1605.1,154.7l132.3-95.9h-164.2l-131.6,95.9h163.4H1605.1z
											M1886.1,655l119.2-86.3l0.3-0.2l-0.1-0.3l0.1,0l133.4,97L2088,822h-164.9l-51-156.9L1886.1,655z M2004.2,567.9l-118.7,86.3
											l-14.1,10.2l-132.9-96.2l8-24l44-132.6h148.9L2004.2,567.9z M1871.1,665.3l50.8,156.4l-133.3-96.7l-0.3-0.2l0,0l-49.3-155.2
											L1871.1,665.3z M1521.8,411.2H1357l-13,39.8l-0.1,0.4L1306,568.1l132.9,96.6l133.9-96.6l-27.3-84.1L1521.8,411.2z M1573.7,568.7
											L1573.7,568.7l-51,156.7l133.5,97l131.3-97l-49.8-156.7H1573.7z M1707.4,980.3l-50.8-156.9l131.6-97.3l134.2,97.3l-50.8,156.9
											H1707.4z M1569.7,49.7l2.7,8.5l-132.5,96.5l-133.4-97l50.8-155.3h164.3L1569.7,49.7z M1521.7,725.3l50.6-155.6l-132.9,95.9
											l-50.6,156.2L1521.7,725.3z M1356.1,410.6l-13.1,40.2l-0.1,0.3l-37.7,116.2l-48.9-151.5l-32.6-102.1h163.9l-31.4,96.6h0
											L1356.1,410.6z M1397.6,283.2L1397.6,283.2l41.8-127.6l-133.4-97l-133.5,97.2l49.2,151.5l0.1,0l1.7,5.3H1388l2.4-7.4L1397.6,283.2
											z M1357.3,410.2l31.3-96.3l131.9,96.3H1357.3z M1222.7,313.6L1222.7,313.6L1171.5,156H1021h-14.8h-0.4H844v0H738.5l-51,157.2
											l-0.1,0.3H687H523l132.5,96.4h165.8l51.2,157.6h165.2l50.9-157.3h164.9L1222.7,313.6z M904.5,155h101L955-0.4L904.5,155z
											M1006.5,155h14.5h150.5l51-156.9l-133.4-97l-133.4,97L1006.5,155z M1255.3,415.8l-1.5-4.6h-164.4l-50.8,156.9l132.9,97l132.9-97
											l-37.7-117l-0.1,0L1255.3,415.8z M37.4,155L37.4,155l-164.9,0l-51-156.9l133.4-97l133.4,97L37.4,155z M38.2,155.9l4.4,13.5
											l46.5,143.2H254l51-156.9l-133.4-97L38.3,155.6l0,0.1L38.2,155.9z M-127.1,156H37.2l4.4,13.7L88.1,313l-132.9,96.2L-177.7,313
											L-127.1,156z M604.1,59.6L604.1,59.6L604.1,59.6l133.4,96l-51,156.9H521.7l-51-156.9L604.1,59.6z M603.8,58.6L603.8,58.6l-164.3,0
											l-1-2.9l-49.8-154l132.9-97l132.9,97L603.8,58.6z M439.1,59.6L439.1,59.6h163.3L469.9,155H307.4L439.1,59.6z M437.6,56l0.9,2.8
											L305.6,155h0L172.3,58.1l51.2-155h164.3L437.6,56z M305.9,156L305.9,156l-51,156.9l69,51.1l64.2,47.5l74.3-55.2l58.4-43.4
											l-51-156.9L305.9,156L305.9,156z M170.8,568.6L37.7,664.8L88.4,821h165.2l50.2-154.1l0.6-1.9L170.8,568.6z M119.6,412.5
											l50.4,155.3L37,664.1l-132.2-94.8l50.8-156.7H119.6z M171.9,1075.7l-0.2,0.1l0.1,0.2h164.9l51-156.9l-125.2-91l-8.2-5.9l-133.4,97
											l1,3.2L171.9,1075.7z M119.9,919.6l1,2.9l49.6,152.9l-132.7,96.1l-132.9-96.2l50.8-155.7H119.9z M263,827.2l124.6,90.2l133.9-94.5
											l-51.8-157.4H305.4l-0.6,1.8l-50,154L263,827.2z M323.4,364.9l-69-51.1l-133.8,98.4L171,567.5h166.1l50.3-155.3L323.4,364.9z
											M438.3,567.5L388.2,413l-50,154.4L438.3,567.5z M438.6,568.5L438.6,568.5l0.4,0h163.7l-132.4,96h-0.2l-133.3-95.7l-0.6,0.8
											l132.2,94.9h-163l0,0l-132.9-96h165.1h0.4l0,0L438.6,568.5z M462.9,357.2l-73.9,54.9l50.4,155.4h164.5l25.9-79.6l25.1-77.3
											l-133.3-96.9L462.9,357.2z M738.6,155l-51-156.9l133.4-97l133.4,97l-51,156.9H738.6z M871.2,569.3l-50.7,156l-132.6,96.4l50.7-156
											L871.2,569.3z M655.7,410.9h164.9l51,156.9l-133.4,97l-133.4-97l25.7-79.2L655.7,410.9z M604.2,568.6l133.4,97l-51,156.9H522.4
											l-51.7-157.1L604.2,568.6z M1038.8,569.5l50.6,155.8l132.6,96.4l-50.8-155.6L1038.8,569.5z M1172.2,665.9l51.1,156.6h164.3
											l50.8-156.9l-133.2-96.8L1172.2,665.9z M687.6,823.2l133.4-97l133.4,97l-51,156.9H738.6l-0.1-0.4l0.2-0.1l-0.1-0.3l-49.9-153.1
											L687.6,823.2z M1033.7,766.4l55.3-40.2l133.4,97l-6.9,21.3l-43.9,134.7l-0.1,0.3l0.2,0.1l-0.2,0.4h-164.9l-51-156.9L1033.7,766.4z
											M1037.4,568.5l51,156.9l-58.7,42.6L955,822.4l-133.4-97l51-156.9H1037.4z M1388.5,825.9l49.8,153.4l-132.7,96.1l-132.8-96.1
											l43.7-134.5l6.9-21.3h164.3L1388.5,825.9z M1439.5,979.7l0.2,0.5h164.9l50.9-156.9l-133.4-97l-133.4,97l0.8,2.4l50.1,153.6
											l0.1,0.3L1439.5,979.7z M737.2,979.3l-49.6-152.8l-1-3H522.3l-50.8,155.7l132.9,96.2L737.2,979.3z M-178.4,821.2l50.8-156H36.7
											l50.7,156l-132.9,96.2L-178.4,821.2z"/>
									
										<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fillRule: 'evenodd', clipRule: 'evenodd', fill: '#C0BDC2' }} d="
											M1005.9,155h166.4l51.4,158.3L1089,411.5l-134.6-98.1L1005.9,155z M1006.6,156L955.7,313l133.4,97.3l133.4-97.3l-51-156.9H1006.6z
											"/>
									
											<radialGradient id="SVGID_00000118374464008211413210000016051196525022001305_" cx="362.4256" cy="913.754" r="1" gradientTransform="matrix(121.274 140.9452 152.3641 -131.0991 -182012.6875 69265.5078)" gradientUnits="userSpaceOnUse">
												<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
											</radialGradient>
									
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000118374464008211413210000016051196525022001305_)', fillOpacity: '0.2' }} d="
											M1089.1,410.7l-51,157.6l133.5,97.4l133.5-97.4l-51-157.6H1089.1z"/>
									<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M1089.1,410.7l-51,157.6
											l133.5,97.4l133.5-97.4l-51-157.6H1089.1z"/>
									<g style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)' }} >
											<path className="st17" d="M737.8,155h166.4l51.4,158.3l-134.6,97.8l-134.6-97.8L737.8,155z M738.5,156l-51,156.9l133.4,97l133.4-97
													l-51-156.9H738.5z"/>
									</g>
									
											<radialGradient id="SVGID_00000021088349080745512850000002429524955952325527_" cx="362.4258" cy="913.7591" r="1" gradientTransform="matrix(121.7284 140.9456 152.4477 -131.6622 -182688.0156 69780.3281)" gradientUnits="userSpaceOnUse">
													<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
													<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
													<stop  offset="1" style={{ stopColor: '#575757' }}/>
										</radialGradient>
									
											<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'url(#SVGID_00000021088349080745512850000002429524955952325527_)', fillOpacity: '0.2' }} d="
											M655.4,410.4L604.2,568l134,97.4l134-97.4L821,410.4H655.4z"/>
									<path style={{ clipPath: 'url(#SVGID_00000043414862164197286610000013805342100403234442_)', fill: 'none' }} d="M655.4,410.4L604.2,568
											l134,97.4l134-97.4L821,410.4H655.4z"/>
							</g>
					</g>
					<g id="Layer_7">
						<g>
							<path className='st19' d='M872.2,568L821,725.6L955,823l134-97.4L1037.8,568H872.2z'/>
						</g>
					</g>
					<g id="Layer_5">
						<g>
							<path className="st19" d="M1006.2,155.5l-51.2,157.6l134,97.7l134-97.7l-51.2-157.6H1006.2z"/>
						</g>
					</g>
					<g id="Layer_6">
						<g>
							<path className="st19" d="M1089.1,410.7l-51,157.6l133.5,97.4l133.5-97.4l-51-157.6H1089.1z"/>
						</g>
					</g>
					<g id="Layer_2">
						<g>
							<g className="st21">
								<radialGradient id="SVGID_00000041296080642290835210000016327118593509305739_" cx="362.4258" cy="913.7591" r="1" gradientTransform="matrix(121.7284 140.9456 152.4477 -131.6622 -182605.1406 69525.4766)" gradientUnits="userSpaceOnUse">
										<stop  offset="0" style={{ stopColor: '#B0B0B0' }}/>
										<stop  offset="0.7708" style={{ stopColor: '#323232' }} />
										<stop  offset="1" style={{ stopColor: '#575757' }}/>
								</radialGradient>
								<path style={{ fill: 'url(#SVGID_00000041296080642290835210000016327118593509305739_)', fillOpacity: '0.2' }} d="M738.3,155.6
									l-51.2,157.6l134,97.4l134-97.4l-51.2-157.6H738.3z"/>
								<path className="st23" d="M738.3,155.6l-51.2,157.6l134,97.4l134-97.4l-51.2-157.6H738.3z"/>
								<path className="st19" d="M738.3,155.6l-51.2,157.6l134,97.4l134-97.4l-51.2-157.6H738.3z"/>
							</g>
						</g>
					</g>
					<g id="Layer_3">
						<g>
							<path className="st19" d="M955,313l-134,97.4L872.2,568h165.6l51.2-157.6L955,313z"/>
							{ /* Central logo (provided ACT_logo_bw.svg), filled, scaled into the pentagon.
							     Same act-orange/act-blue/act-yellow classes as the navbar Logo
							     (assets/icons.js) - hover-to-color rule for #Layer_3 lives in
							     base.scss, base fill stays the page's literal off-white. */ }
							<g transform='translate(882.8,377.9) scale(0.0923)' stroke='none'>
								<path className='act-orange' d='M757.677 303.707C772.177 293.09 791.988 293.09 806.488 303.707L806.489 303.708L1307.19 668.388V668.389C1325.57 681.855 1329.72 707.619 1316.25 726.133C1302.79 744.52 1277.02 748.662 1258.51 735.197H1258.51L782.442 388.468L782.147 388.253L781.853 388.468L332.65 717.246L332.358 717.459L332.471 717.804L505.442 1248.64L505.555 1248.99H1092.06C1114.84 1248.99 1133.36 1267.5 1133.36 1290.55C1133.36 1313.34 1114.84 1331.85 1092.06 1331.85H475.517C457.52 1331.85 441.724 1320.33 436.156 1303.24L244.446 714.612C238.879 697.523 244.961 679.007 259.334 668.388L259.335 668.389L757.677 303.708V303.707Z' />
								<path className='act-blue' d='M756.128 42.3057C770.628 31.6892 790.439 31.6892 804.939 42.3057L804.94 42.3066L1547.41 583.106L1547.41 583.107C1561.91 593.594 1567.99 612.238 1562.56 629.197L1278.33 1501.92C1272.64 1519.4 1256.45 1530.53 1238.97 1530.53C1234.7 1530.53 1230.43 1529.89 1226.16 1528.46L1226.16 1528.46L1225.14 1528.12C1204.03 1520.61 1192.6 1497.69 1199.61 1476.28V1476.28L1474.4 632.521L1474.51 632.176L1474.22 631.962L781.024 127.198L780.729 126.983L780.435 127.199L90.9072 631.963L90.6152 632.177L90.7275 632.521L365.39 1476.28C372.40 1497.69 360.97 1520.61 339.86 1528.12L338.84 1528.46C334.57 1529.89 330.30 1530.53 326.03 1530.53C308.55 1530.53 292.36 1519.40 286.67 1501.92L2.57129 629.328C-2.99642 612.236 2.95928 593.723 17.4609 583.105L756.128 42.3057Z' />
								<path className='act-yellow' d='M783.585 545.177H783.847C785.162 545.177 786.314 545.177 787.461 545.305L787.488 545.308H787.516C787.671 545.308 787.86 545.334 788.121 545.369C788.351 545.4 788.624 545.433 788.905 545.437C789.282 545.5 789.63 545.563 789.981 545.627C790.335 545.691 790.692 545.755 791.08 545.82C791.345 545.886 791.641 545.953 791.928 546.017C792.224 546.082 792.513 546.146 792.768 546.21L792.778 546.213L792.79 546.216C793.083 546.274 793.349 546.363 793.647 546.462C793.939 546.559 794.262 546.667 794.625 546.739C794.924 546.799 795.23 546.891 795.539 546.988C795.837 547.082 796.155 547.188 796.437 547.259L796.448 547.262L796.46 547.264C797.062 547.384 797.674 547.627 798.338 547.893L798.348 547.896C799.396 548.289 800.416 548.673 801.434 549.181C801.664 549.328 801.918 549.437 802.126 549.526C802.364 549.628 802.55 549.71 802.701 549.811L802.711 549.816L802.721 549.823C803.056 550.025 803.359 550.192 803.653 550.355C803.949 550.519 804.236 550.68 804.556 550.871C804.764 551.009 805.001 551.145 805.22 551.27C805.453 551.403 805.666 551.525 805.846 551.645L805.855 551.651L805.866 551.657C806.528 552.054 807.159 552.433 807.658 552.808L807.791 552.908H807.958C807.981 552.908 807.993 552.911 807.997 552.912C808.001 552.913 808.005 552.915 808.012 552.919C808.019 552.923 808.031 552.932 808.051 552.949C808.072 552.968 808.094 552.989 808.129 553.023L808.155 553.05L808.185 553.072L1086.51 758.804L1086.51 758.805C1104.61 772.058 1108.78 797.232 1096.06 815.804L1095.44 816.684C1087.42 827.814 1074.73 833.643 1062.04 833.643C1053.63 833.643 1045.08 831.054 1037.7 825.616H1037.71L825.946 668.631L825.148 668.04V1153.12A41.367 41.367 0 0 1 742.414 1153.12V667.649L741.617 668.237L526.453 827.056C508.069 840.52 482.175 836.507 468.71 817.992L468.709 817.991L468.09 817.124C455.373 798.801 459.547 773.502 477.772 760.247L477.774 760.246L759.508 553.073L759.538 553.051L759.565 553.023C759.645 552.944 759.754 552.876 759.928 552.771C760.057 552.694 760.231 552.588 760.392 552.448C760.602 552.365 760.776 552.261 760.914 552.169C761.096 552.048 761.176 551.985 761.27 551.938L761.298 551.924L761.323 551.907C762.115 551.379 762.875 550.874 763.628 550.497C763.796 550.413 763.979 550.278 764.092 550.203C764.226 550.114 764.347 550.045 764.48 550L764.544 549.979L764.6 549.941C765.341 549.447 766.214 549.072 767.14 548.675L767.153 548.669L767.166 548.662C767.297 548.597 767.429 548.531 767.56 548.466C767.688 548.401 767.817 548.336 767.945 548.271C769.095 547.762 770.247 547.378 771.425 546.985L771.459 546.975L771.49 546.959C771.55 546.929 771.643 546.906 771.824 546.87C771.956 546.844 772.159 546.803 772.347 546.722C773.119 546.464 774.003 546.214 774.876 546.089L774.921 546.083L774.963 546.068C775.108 546.02 775.291 545.992 775.531 545.958C775.735 545.929 775.988 545.892 776.223 545.82C776.994 545.692 777.761 545.564 778.651 545.437C778.935 545.433 779.181 545.399 779.386 545.367C779.612 545.332 779.766 545.308 779.916 545.308C780.521 545.308 781.157 545.274 781.776 545.241C782.402 545.208 783.011 545.177 783.585 545.177Z' />
							</g>
						</g>
					</g>
					<g id="Layer_4">
						<g>
							<path className="st19" d="M655.4,410.4L604.2,568l134,97.4l134-97.4L821,410.4H655.4z"/>
						</g>
					</g>
						{ /* Runic "ACT" wordmark, centered between the logo and the
						     pentagon's lower edge (AC = two part-pentagons, T = arrow) */ }
						<g
							transform='translate(909.78,518.3) scale(0.0942)'
							stroke='#FFFFFF'
							strokeWidth='24'
							fill='none'
							strokeLinecap='round'
							strokeLinejoin='round'
							pointerEvents='none'
						>
							{ /* each glyph scaled 0.8 about its own centre -> wider letter spacing */ }
							<g transform='translate(31.8,35.3) scale(0.8)'>
								<path d='m19.963549 145.64894l139.41704 -101.22374l139.41702 101.22374l-53.252563 163.78346' />
								<path d='m73.21613 309.43240l-53.252581 -163.783460' />
							</g>
							<g transform='translate(96,35.3) scale(0.8)'>
								<path d='m340.58298 145.64894l139.41702 -101.22374l139.41705 101.22374' />
								<path d='m566.16447 309.43240l-172.32895 0l-53.25254 -163.78346' />
							</g>
							<g transform='translate(160,35.3) scale(0.8)'>
								<path d='m800.61945 44.37767l0 265.10236' />
								<path d='m800.61945 44.37767l139.43304 101.291336' />
								<path d='m661.2021 145.66928l139.4331 -101.291336' />
							</g>
						</g>
					{ /* Menu labels (Montserrat, replacing the old vector glyphs; small numbers dropped) */ }
					<g fill="#FFFFFF" fontFamily="Montserrat, Roboto, 'Helvetica Neue', Arial, sans-serif" fontWeight="700" fontSize="32" textAnchor="middle" dominantBaseline="central" pointerEvents="none">
						<text x="821" y="270">AI</text>
						<text x="737" y="524">TECH</text>
						<text x="1090" y="270">ACT</text>
						<text x="1171" y="524">OPEN</text>
						<text x="955" y="685">TEAM</text>
					</g>
				</svg>
				<Box
					component='span'
					sx={ {
						position: 'absolute',
						left: 32,
						bottom: 24,
						color: '#8a8a8a',
						fontSize: 11,
						lineHeight: 1.5,
						letterSpacing: '0.08em',
						textTransform: 'uppercase',
						pointerEvents: 'none'
					} }
				>
					© { year }<br />AMTOSS / ACT
				</Box>
			</Box>
			<Box
				ref={ container }
				className='home'
				sx={ {
					opacity: isComplete ? 0 : 1,
					pointerEvents: isComplete ? 'none' : 'auto',
					transition: 'opacity 0.6s ease-in-out',
					// Nudge the animation up-left so its end frame lines up with the
					// static menu (which sits ~4px left / ~10px above the lottie),
					// removing the small position jump at the cross-fade.
					transform: 'translate(-4px, -10px)',
					position: 'absolute',
					top: 0,
					bottom: 0,
					left: 0,
					right: 0
				} }
			/>
		</Box>
	)
}

export default Home
